// Template 5: Black Marble
// Modern luxury with texture contrast - black marble + gold accents

import { useState } from 'react'
import { 
  Phone, 
  MessageCircle, 
  Mail, 
  Share2, 
  Download,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  Globe,
  MapPin,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'

export function BlackMarbleTemplate({ vcard, onSaveContact, onShare }) {
  const [activeSection, setActiveSection] = useState(null)
  const [galleryIndex, setGalleryIndex] = useState(0)

  const handleSectionTap = (section) => {
    setActiveSection(section)
    setTimeout(() => setActiveSection(null), 500)
  }

  const nextImage = () => {
    if (vcard.gallery && vcard.gallery.length > 0) {
      setGalleryIndex((prev) => (prev + 1) % vcard.gallery.length)
    }
  }

  const prevImage = () => {
    if (vcard.gallery && vcard.gallery.length > 0) {
      setGalleryIndex((prev) => (prev - 1 + vcard.gallery.length) % vcard.gallery.length)
    }
  }

  return (
    <div className="black-marble-template">
      {/* Marble Background */}
      <div className="bm-marble-bg"></div>
      
      <div className="bm-container">
        {/* Profile Section */}
        <section 
          className={`bm-section bm-profile ${activeSection === 'profile' ? 'highlight' : ''}`}
          onClick={() => handleSectionTap('profile')}
        >
          <div 
            className="bm-avatar"
            style={{ 
              backgroundImage: vcard.profileImage ? `url(${vcard.profileImage})` : 'none'
            }}
          >
            {!vcard.profileImage && (
              <span>{vcard.name?.[0] || 'U'}</span>
            )}
          </div>
          <h1 className="bm-name">{vcard.name || 'Your Name'}</h1>
          <p className="bm-title">{vcard.title || 'Your Title'}</p>
          {vcard.company && <p className="bm-company">{vcard.company}</p>}
        </section>

        <div className="bm-gold-line"></div>

        {/* Actions */}
        <section 
          className={`bm-section bm-actions ${activeSection === 'actions' ? 'highlight' : ''}`}
          onClick={() => handleSectionTap('actions')}
        >
          {vcard.phone && (
            <a href={`tel:${vcard.phone}`} className="bm-action-btn">
              <Phone size={18} strokeWidth={1.5} />
            </a>
          )}
          {vcard.whatsapp && (
            <a 
              href={`https://wa.me/${vcard.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bm-action-btn"
            >
              <MessageCircle size={18} strokeWidth={1.5} />
            </a>
          )}
          {vcard.email && (
            <a href={`mailto:${vcard.email}`} className="bm-action-btn">
              <Mail size={18} strokeWidth={1.5} />
            </a>
          )}
          <button onClick={(e) => { e.stopPropagation(); onShare(); }} className="bm-action-btn">
            <Share2 size={18} strokeWidth={1.5} />
          </button>
        </section>

        <div className="bm-gold-line"></div>

        {/* About */}
        {vcard.about && (
          <>
            <section 
              className={`bm-section bm-about ${activeSection === 'about' ? 'highlight' : ''}`}
              onClick={() => handleSectionTap('about')}
            >
              <p>{vcard.about}</p>
            </section>
            <div className="bm-gold-line"></div>
          </>
        )}

        {/* Services */}
        {vcard.services && vcard.services.length > 0 && (
          <>
            <section 
              className={`bm-section bm-expertise ${activeSection === 'services' ? 'highlight' : ''}`}
              onClick={() => handleSectionTap('services')}
            >
              <h3 className="bm-label">Expertise</h3>
              <div className="bm-tags">
                {vcard.services.map((service, i) => (
                  <span key={i} className="bm-tag">{service}</span>
                ))}
              </div>
            </section>
            <div className="bm-gold-line"></div>
          </>
        )}

        {/* Gallery - Gold Framed Tiles */}
        {vcard.gallery && vcard.gallery.length > 0 && (
          <>
            <section 
              className={`bm-section bm-gallery ${activeSection === 'gallery' ? 'highlight' : ''}`}
              onClick={() => handleSectionTap('gallery')}
            >
              <h3 className="bm-label">Portfolio</h3>
              <div className="bm-gallery-wrapper">
                <button className="bm-gallery-nav prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}>
                  <ChevronLeft size={20} />
                </button>
                <div className="bm-gallery-frame">
                  <img 
                    src={vcard.gallery[galleryIndex]} 
                    alt={`Portfolio ${galleryIndex + 1}`}
                  />
                </div>
                <button className="bm-gallery-nav next" onClick={(e) => { e.stopPropagation(); nextImage(); }}>
                  <ChevronRight size={20} />
                </button>
              </div>
              <div className="bm-gallery-dots">
                {vcard.gallery.map((_, i) => (
                  <span 
                    key={i} 
                    className={`bm-dot ${i === galleryIndex ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setGalleryIndex(i); }}
                  ></span>
                ))}
              </div>
            </section>
            <div className="bm-gold-line"></div>
          </>
        )}

        {/* Social Links */}
        {vcard.social && Object.values(vcard.social).some(v => v) && (
          <>
            <section 
              className={`bm-section bm-social ${activeSection === 'social' ? 'highlight' : ''}`}
              onClick={() => handleSectionTap('social')}
            >
              {vcard.social.linkedin && (
                <a 
                  href={vcard.social.linkedin.startsWith('http') ? vcard.social.linkedin : `https://linkedin.com/in/${vcard.social.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bm-social-icon"
                >
                  <Linkedin size={18} strokeWidth={1.5} />
                </a>
              )}
              {vcard.social.instagram && (
                <a 
                  href={vcard.social.instagram.startsWith('http') ? vcard.social.instagram : `https://instagram.com/${vcard.social.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bm-social-icon"
                >
                  <Instagram size={18} strokeWidth={1.5} />
                </a>
              )}
              {vcard.social.facebook && (
                <a 
                  href={vcard.social.facebook.startsWith('http') ? vcard.social.facebook : `https://facebook.com/${vcard.social.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bm-social-icon"
                >
                  <Facebook size={18} strokeWidth={1.5} />
                </a>
              )}
              {vcard.social.twitter && (
                <a 
                  href={vcard.social.twitter.startsWith('http') ? vcard.social.twitter : `https://twitter.com/${vcard.social.twitter.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bm-social-icon"
                >
                  <Twitter size={18} strokeWidth={1.5} />
                </a>
              )}
              {vcard.social.website && (
                <a 
                  href={vcard.social.website.startsWith('http') ? vcard.social.website : `https://${vcard.social.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bm-social-icon"
                >
                  <Globe size={18} strokeWidth={1.5} />
                </a>
              )}
            </section>
            <div className="bm-gold-line"></div>
          </>
        )}

        {/* Location */}
        {vcard.address && (
          <>
            <a 
              href={`https://maps.google.com/?q=${encodeURIComponent(vcard.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bm-section bm-location"
            >
              <MapPin size={16} strokeWidth={1.5} />
              <span>{vcard.address}</span>
            </a>
            <div className="bm-gold-line"></div>
          </>
        )}

        {/* Save Button */}
        <button onClick={onSaveContact} className="bm-save-btn">
          <Download size={18} strokeWidth={1.5} />
          <span>Save Contact</span>
        </button>

        {/* Footer */}
        <footer className="bm-footer">
          <p>Crafted with <a href="/">Digital vCard</a></p>
        </footer>
      </div>
    </div>
  )
}

export default BlackMarbleTemplate
